// Overview — 표시 포맷 / 색 파생 헬퍼.
//
// 원칙:
//   · 색은 LCMD 시맨틱 토큰(Tailwind 유틸 or var(--…))으로. 인라인 hex 금지.
//     예외: 크리에이터별 themeColor(데이터값)에서 hexA로 파생하는 히어로 틴트뿐.
//   · 나눗셈 지표(watchRate·engagement)는 비유한값(NaN/Infinity/null)이면 "—".
import type { DataMeta, Platform } from '../../creatorsApi'

export const PLATFORM_LABEL: Record<Platform, string> = {
  youtube: 'YouTube',
  instagram: 'Instagram',
  tiktok: 'TikTok',
}

/** 1자리 소수 — 정수면 소수점 제거. 12.0 → "12", 12.3 → "12.3". */
function trimDecimal(v: number): string {
  const s = v.toFixed(1)
  return s.endsWith('.0') ? s.slice(0, -2) : s
}

/** 구독자/조회수 등 큰 수 → 한국어 단위(만·억). 84.2만 · 129만 · 1.2억. */
export function formatCount(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—'
  const abs = Math.abs(n)
  if (abs >= 1e8) return `${trimDecimal(n / 1e8)}억`
  if (abs >= 1e4) return `${trimDecimal(n / 1e4)}만`
  return Math.round(n).toLocaleString('ko-KR')
}

/** 비율(0~1+) → 퍼센트. 숏폼은 100% 초과 정상(상한 없음). 분모0/null 가드 → "—".
 *  단 10배(=1,000%) 이상은 "×12.4" 배수 표기로 전환한다 — `12,400%` 같은 값은 매니저가
 *  오류로 판단하고 화면 전체를 불신하기 때문(값은 그대로, 읽는 단위만 바꾼다). */
export function formatRate(ratio: number | null | undefined): string {
  if (ratio == null || !Number.isFinite(ratio)) return '—'
  if (Math.abs(ratio) >= 10) return `×${trimDecimal(ratio)}`
  return `${trimDecimal(ratio * 100)}%`
}

/** "2025-09" → "9월" (매출 차트 축). 파싱 불가면 원문. */
export function formatMonthShort(ym: string): string {
  const m = Number(ym.slice(5, 7))
  return Number.isFinite(m) && m >= 1 && m <= 12 ? `${m}월` : ym
}

/** "2026-02" → "2026.02" (Confirmed 딜 표기). 포맷 불일치면 원문. */
export function formatYearMonth(ym: string): string {
  return /^\d{4}-\d{2}$/.test(ym) ? ym.replace('-', '.') : ym
}

/** "2026-08-09" → "2026.08.09" (수집 기준일 표기). 값 없거나 포맷 불일치면 null → 문구에서 조각 자체가 빠진다. */
export function formatIsoDate(d: string | null | undefined): string | null {
  if (!d) return null
  const m = d.slice(0, 10)
  return /^\d{4}-\d{2}-\d{2}$/.test(m) ? m.replace(/-/g, '.') : null
}

/** YouTube embed URL(youtube.com/embed/{id})을 일반 시청 페이지(watch?v={id})로 변환.
 *  BE url 원값은 플레이어 전용 embed 형식 → 클릭 타깃은 일반 시청 페이지가 자연스러움.
 *  embed 패턴이 아니면(Instagram 등) 원값 그대로. 쿼리/프래그먼트는 보존. 표시 레이어 전용. */
export function toWatchUrl(url: string): string {
  const m = url.match(/^(https?:\/\/(?:www\.)?youtube\.com)\/embed\/([^/?#]+)([?#].*)?$/i)
  if (!m) return url
  const [, origin, id, tail = ''] = m
  const suffix = tail.startsWith('?') ? `&${tail.slice(1)}` : tail // #frag 은 그대로 뒤에 붙음
  return `${origin}/watch?v=${id}${suffix}`
}

/** 집계 고지 1줄 — "소스 · 집계창 · 수집 기준일" 3요소. SNS 파생 수치 블록은 이 문구로
 *  자기 근거를 밝힌다(A2).
 *
 *  집계창 조각의 분기가 중요하다:
 *    · `meta` 자체가 없다(구버전 BE) → **조각 생략**. 모르는 것을 말하지 않는다(R9).
 *    · `meta`는 왔는데 `windowMonths`가 null → **"전체 기간"**. null은 "모른다"가 아니라
 *      "창이 없다"는 사실이다(대표 콘텐츠처럼 기간 제한 없이 뽑는 블록). 여기서 생략하면
 *      기간 정보가 통째로 빠진 고지가 되어 A2의 3요소가 깨진다.
 *  수집 기준일은 값이 있을 때만 붙는다 — 없는 날짜를 지어내지 않는다. */
export function buildBasisNote(source: string, meta?: DataMeta): string {
  return [source, ...basisFragments(meta)].join(' · ')
}

/** 근거 조각만 필요한 자리(사유 목록 뒤에 붙이는 각주 등)를 위한 분리형.
 *  ⚠️ 이 규칙을 호출부에서 다시 구현하지 마라 — 한 곳이라도 어긋나면 같은 화면이 서로 다른
 *  기간을 주장한다(실제로 채널 퍼포먼스 각주가 그렇게 어긋나 검증에서 잡혔다). */
export function basisFragments(meta?: DataMeta): string[] {
  if (!meta) return []
  // ⚠️ "수집 YYYY.MM.DD" 를 여기에 되살리지 마라. 그 값의 원천은 `MAX(update_at)` — "수집한 시각"이
  //    아니라 **행이 마지막으로 갱신된 시각**이다. 실측(크리에이터 246): bi_base_data 의 update_at 은
  //    2025-10-14 에 멈춰 있는데 그 안의 발행일은 2026-07-31 까지 있다. 그대로 "수집 2025.10.14" 라
  //    적으면 최신 데이터를 10개월 묵은 것으로 단언하게 된다. 라벨이 값보다 강한 주장을 했다.
  return [meta.windowMonths ? `최근 ${meta.windowMonths}개월` : '전체 기간']
}

/**
 * AI 판독 고지 — Instagram·TikTok 은 **광고 여부·브랜드명·상품명**을 캡션 AI 가 판독한다.
 *
 * 사람이 확인한 사실이 아니므로 그 값이 노출되는 자리마다 밝힌다. 문구가 대상을 명시하는 이유는
 * **숫자까지 싸잡지 않기 위해서**다 — 조회수·좋아요·댓글·구독자는 플랫폼 원본이고 AI 와 무관하다.
 * YouTube 는 사람이 확정한다(bi_base_data 수동 매칭) — 그래서 안 붙인다.
 * 사람 확정은 YouTube 에만 있고 IG·TikTok 은 전량 AI 판독이라, 플랫폼만 보면 판정이 끝난다
 * (행마다 출처를 조회할 필요가 없다).
 */
export const AI_JUDGED_NOTE = '브랜디드 여부·브랜드·상품명은 캡션 AI 판독'

// ⚠️ 이 문구는 **고지 줄(AiJudgedNotice)에서만** 쓴다. ⓘ·title 같은 hover 문구에 섞으면
//    같은 말이 두 자리에서 반복되고, hover 문구가 길어져 정작 산식·집계창이 묻힌다.

/** hex(#rrggbb) → rgba 문자열. 크리에이터 themeColor에서 틴트 파생용. 유효하지 않으면 null. */
export function hexA(hex: string | undefined | null, alpha: number): string | null {
  if (!hex) return null
  const n = hex.replace('#', '')
  if (!/^[0-9a-fA-F]{6}$/.test(n)) return null
  const r = parseInt(n.slice(0, 2), 16)
  const g = parseInt(n.slice(2, 4), 16)
  const b = parseInt(n.slice(4, 6), 16)
  return `rgba(${r},${g},${b},${alpha})`
}
